import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
} from '@angular/core'
import { MatRadioChange } from '@angular/material/radio'
import {
  Question,
  QuestionAnswer,
  QuestionImageType,
  QuestionInstructionLocation,
  QuestionTickerType,
} from 'src/app/interfaces/question'
import { GeneralService } from 'src/app/services/general/general.service'
import { QuestionAnswerOption } from './../../../../interfaces/question'
import { QuestionService } from './../../../../services/question/question.service'

@Component({
  selector: 'question-linear',
  templateUrl: './linear-question.component.html',
  styleUrls: ['../../../../../theme/radio-button.scss', './linear-question.component.scss'],
})
export class LinearQuestionComponent implements OnInit {
  @ContentChild(TemplateRef) templateRef: TemplateRef<any> | null = null
  private readonly defaultSelectedColor = '#0E4473'
  @HostBinding('style.--selected-button-color') selectedButtonColor = this.defaultSelectedColor
  @HostBinding('style.--button-size') buttonSize
  @HostBinding('style.--inner-circle-scale') innerCicleScale

  @Input() question: Question | undefined
  @Input() disabled = false
  @Input() checkedOption: number | undefined // 이미 선택된 옵션 (option id)
  @Input() compareOption: number | undefined // 비교 옵션 (option id)
  @Input() isVariedCircleSize = false // 증감 형태의 circle button일 경우
  @Input() otherSideSelectedColor = 'var(--red-30)' // 비교 옵션이 있을 경우, 비교 값과 다른 사이드를 선택 시 컬러
  @Input() imageType: QuestionImageType | undefined
  @Input() tickerSize: 'small' | 'medium' | 'large' = 'large'
  @Input() tickerType: QuestionTickerType = 'default'
  @Input() showTickerToggleIcon = false
  @Input() tickerBackgroundColor: string | undefined
  @Output() answered = new EventEmitter<QuestionAnswer>()

  leftExample = ''
  leftImage: string | undefined
  rightExample = ''
  rightImage: string | undefined
  leftQuestionSelected = false
  rightQuestionSelected = false
  questionImageType = QuestionImageType
  // host와 다른 방향 선택 여부
  otherSideSelected: boolean | undefined

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private generalService: GeneralService,
    private questionService: QuestionService
  ) {}

  // 선택된 질문이 오른쪽인지 왼쪽인지
  get selectedQuestion(): 'left' | 'right' | 'center' {
    if (this.leftQuestionSelected) {
      return 'left'
    }
    if (this.rightQuestionSelected) {
      return 'right'
    }
    return 'center'
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('question') && this.question !== undefined) {
      this.initializeData()
      this.setInstructionValues()
      const setStyleTimeout = setTimeout(() => {
        this.setButtonSize()
        this.setButtonInnerCircleScale()
        clearTimeout(setStyleTimeout)
      }, 100)
    }
    if (changes.hasOwnProperty('checkedOption') && this.checkedOption !== undefined && this.question !== undefined) {
      this.setQuestionSelectedValuesWithSelectedOptionId()
    }

    // 비교옵션과 다를 경우
    if (
      changes.hasOwnProperty('compareOption') &&
      this.compareOption !== undefined &&
      this.question !== undefined &&
      this.checkedOption !== this.compareOption
    ) {
      this.setQuestionSelectedValuesWithSelectedOptionId()
      const setCompareOptionTimeout = setTimeout(() => {
        const elem = this.elementRef.nativeElement.querySelector(
          `.radio-button-${this.question?.id ?? 0}-${this.compareOption}`
        )

        if (elem !== undefined && elem !== null) {
          this.renderer.addClass(elem, 'mat-radio-checked')
          this.renderer.addClass(elem, 'mat-radio-compare-checked')
        }
        clearTimeout(setCompareOptionTimeout)
      }, 100)
    }

    // 비교옵션과 같을 경우 compare-checked class 제거
    if (
      changes.hasOwnProperty('compareOption') &&
      this.compareOption !== undefined &&
      this.question !== undefined &&
      this.checkedOption === this.compareOption
    ) {
      this.setQuestionSelectedValuesWithSelectedOptionId()
      const setCompareOptionTimeout = setTimeout(() => {
        const elem = this.elementRef.nativeElement.querySelector(
          `.radio-button-${this.question?.id ?? 0}-${this.compareOption}`
        )

        if (elem !== undefined && elem !== null) {
          this.renderer.addClass(elem, 'mat-radio-checked')
          this.renderer.removeClass(elem, 'mat-radio-compare-checked')
        }
        clearTimeout(setCompareOptionTimeout)
      }, 100)
    }
  }

  onSelected($event: MatRadioChange): void {
    this.setSelectedData($event.value)
  }

  onClickTicker(location: 'left' | 'right'): void {
    if (this.question === undefined) {
      return
    }
    this.questionService.excludeClicked({
      question: this.question,
      location,
    })
  }

  private initializeData(): void {
    this.leftQuestionSelected = false
    this.rightQuestionSelected = false
  }

  private setSelectedData(option: QuestionAnswerOption): void {
    if (this.question === undefined) {
      return
    }

    const selectedOption: QuestionAnswerOption = option
    this.answered.emit({
      ...selectedOption,
      options: [selectedOption.id],
      questionAnswerSet: [
        {
          question: this.question,
          metaRelations: [],
        },
      ],
    })
    this.checkedOption = selectedOption.id

    const selectedIndex = this.question.answerOptions?.findIndex((option) => option.id === selectedOption.id)
    if (selectedIndex !== undefined) {
      this.setQuestionSelectedValues(selectedIndex)
    }
  }

  private setInstructionValues(): void {
    if (this.question !== undefined && Array.isArray(this.question.instructions)) {
      this.question.instructions.forEach((inst) => {
        switch (inst.location) {
          case QuestionInstructionLocation.LEFT:
            this.leftExample = inst.content
            break
          case QuestionInstructionLocation.RIGHT:
            this.rightExample = inst.content
            break
          case QuestionInstructionLocation.IMAGE_LEFT:
            this.leftImage = inst.content
            break
          case QuestionInstructionLocation.IMAGE_RIGHT:
            this.rightImage = inst.content
        }
      })
    }
  }

  /** 옵션 선택시, set button colors */
  private setQuestionSelectedValues(selectedValue: number): void {
    if (this.question !== undefined && this.question !== null && Array.isArray(this.question.answerOptions)) {
      const mid = this.question.answerOptions.length / 2
      const compareOptionIndex = this.question.answerOptions?.findIndex((option) => option.id === this.compareOption)
      this.selectedButtonColor = this.defaultSelectedColor
      if (
        this.question.answerOptions.length % 2 === 1 &&
        selectedValue + 1 === Math.round(this.question.answerOptions.length / 2)
      ) {
        this.leftQuestionSelected = false
        this.rightQuestionSelected = false
      } else if (selectedValue < this.question.answerOptions.length / 2) {
        if (compareOptionIndex >= 0 && compareOptionIndex >= mid) {
          this.selectedButtonColor = this.otherSideSelectedColor
          this.otherSideSelected = true
        }
        this.leftQuestionSelected = true
        this.rightQuestionSelected = false
      } else {
        if (compareOptionIndex >= 0 && compareOptionIndex < mid) {
          this.selectedButtonColor = this.otherSideSelectedColor
          this.otherSideSelected = true
        }
        this.rightQuestionSelected = true
        this.leftQuestionSelected = false
      }
    }
  }

  /** checked option id를 이용해서 setQuestionSelectedValues() 함수 수행 */
  private setQuestionSelectedValuesWithSelectedOptionId(): void {
    const selectedIndex = this.question?.answerOptions?.findIndex((option) => option.id === this.checkedOption)
    if (selectedIndex !== undefined && selectedIndex !== null && selectedIndex >= 0) {
      this.setQuestionSelectedValues(selectedIndex)
    }
  }

  private setButtonSize(): void {
    if (this.question === undefined || !Array.isArray(this.question.answerOptions)) {
      return
    }
    const setSize = (el: HTMLElement, index: number, length: number, isContainer = false) => {
      const baseSizeGap = 4 // 인접 버튼간의 사이즈 크기 차이
      const size = maxButtonSize
      let sizeGap = 0
      // 증감 circle size일 경우에만 수행. 아닐경우는 sizeGap 무조건 0
      if (this.isVariedCircleSize) {
        if (index < length / 2) {
          sizeGap = baseSizeGap * index
        } else {
          sizeGap = baseSizeGap * Math.abs(length - 1 - index)
        }
      }
      const buttonSize = Math.min(size - sizeGap, maxButtonSize)
      // width가 소수점일 경우, offsetWidth로 하면 정수만 추출됨. 그렇기에 다시 width를 정수로 세팅.
      this.renderer.setStyle(el, 'height', `${buttonSize}px`)
      this.renderer.setStyle(el, 'width', `${buttonSize}px`)
      if (isContainer) {
        buttonSizeList.push(buttonSize)
      }
    }

    const radioButtonWrapperElem = this.element('.linear-radio-button-list')
    const totalWidth = radioButtonWrapperElem?.offsetWidth ?? 0
    const maxButtonSize = this.maxButtonSize(totalWidth, this.question.answerOptions.length)

    const radioButtonContainerElems = this.elementList('.mat-radio-container')
    const radioButtonOuterCircleElems = this.elementList('.mat-radio-outer-circle')
    const radioButtonInnerCircleElems = this.elementList('.mat-radio-inner-circle')

    const buttonSizeList: number[] = []
    radioButtonContainerElems.forEach((el, index) => setSize(el, index, radioButtonContainerElems.length, true))
    radioButtonOuterCircleElems.forEach((el, index) => setSize(el, index, radioButtonOuterCircleElems.length))
    radioButtonInnerCircleElems.forEach((el, index) => setSize(el, index, radioButtonInnerCircleElems.length))

    // width 재 조정 위한 작업. 안해줄경우(1fr로만 할 경우) 버튼이 너무 적으면 빈공간이 많아짐;
    if (this.isVariedCircleSize) {
      this.renderer.setStyle(radioButtonWrapperElem, 'grid-template-columns', `${buttonSizeList.join('px ')}px`)
    } else {
      this.renderer.setStyle(
        radioButtonWrapperElem,
        'grid-template-columns',
        `repeat(${this.question?.answerOptions?.length ?? 0}, ${Math.min(buttonSizeList[0], maxButtonSize)}px)`
      )
    }
  }

  /** button size가 많아질 경우 inner cicle이 너무 커지는 문제 해결 */
  private setButtonInnerCircleScale(): void {
    if (this.question === undefined) {
      return
    }
    const buttonElem: HTMLElement | null = this.element('mat-radio-button')
    if (buttonElem !== undefined && buttonElem !== null) {
      const optionsCount = Array.isArray(this.question.answerOptions) ? this.question.answerOptions.length : 0
      const baseOptionsCount = 6
      const baseInnerCicleScale = 0.6
      if (optionsCount > baseOptionsCount) {
        const diff = optionsCount - baseOptionsCount
        this.innerCicleScale = baseInnerCicleScale - 0.05 * diff
      }
    }
  }

  // 최대 circle size 값 계산
  private maxButtonSize(wrapperWidth: number, buttonCount: number): number {
    if (this.isVariedCircleSize) {
      return this.maxButtonSizeForVariedCircle(wrapperWidth, buttonCount)
    }

    const maxSize = 40
    const gap = 8
    const totalGap = (buttonCount - 1) * gap
    let buttonSize = maxSize

    while (true) {
      let totalWidth = totalGap + buttonSize * buttonCount
      if (wrapperWidth >= totalWidth) {
        break
      }
      buttonSize -= 1
    }
    return buttonSize
  }

  private maxButtonSizeForVariedCircle(wrapperWidth: number, buttonCount: number): number {
    const maxSize = 52
    const gap = 8
    const circleGap = 4
    const totalGap = (buttonCount - 1) * gap
    let buttonSize = maxSize
    while (true) {
      const mid = Math.floor(buttonCount / 2)
      const midValue = ((mid - 1) * circleGap) / 2
      let totalCircleGap = 0
      // 홀수일 경우 제일 중간 값 더하기
      if (buttonCount % 2 === 1) {
        totalCircleGap = midValue * (buttonCount - 1) + mid * circleGap
      } else {
        totalCircleGap = midValue * buttonCount
      }
      let totalWidth = totalGap + buttonSize * buttonCount - totalCircleGap
      if (wrapperWidth >= totalWidth) {
        break
      }
      buttonSize -= 1
    }
    return buttonSize
  }

  private element(className: string): HTMLElement | null {
    return this.elementRef.nativeElement.querySelector(className)
  }

  private elementList(className: string): HTMLElement[] {
    return this.elementRef.nativeElement.querySelectorAll(className)
  }
}
